/**
 * Shared editor / app chrome palette — single source for top bars, nav, side panels.
 * Orange accent (#fe9a00) is reserved for selection/active states elsewhere.
 */

import { CHROME_ICON_HIT, CONTROL_RADIUS } from "./uiChromeTokens";

const UI_FONT =
  "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export { UI_FONT };

/** Shared top-bar icon cluster dimensions */
export const CHROME_TOOLBAR_GROUP_HEIGHT = 32;
export const CHROME_TOOLBAR_GROUPS_GAP = 8;
export const CHROME_TOOLBAR_ICON_GLYPH = 17;
export const CHROME_TOOLBAR_GROUP_PADDING = "2px 4px";

/** Bordered cluster wrapping one or more toolbar icons (undo/redo, viewport, actions). */
export function chromeToolbarGroupStyle(iconColour) {
  return {
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "nowrap",
    height: CHROME_TOOLBAR_GROUP_HEIGHT,
    boxSizing: "border-box",
    border: `1px solid ${iconColour?.border ?? "#E7E5E4"}`,
    borderRadius: CONTROL_RADIUS,
    padding: CHROME_TOOLBAR_GROUP_PADDING,
    gap: 0,
  };
}

/** Mantine ActionIcon props for a single toolbar glyph. */
export function chromeToolbarIconButtonProps(hover, iconColour, disabled = false, inGroup = false) {
  const canHover = hover && !disabled;
  return {
    size: CHROME_ICON_HIT,
    radius: CONTROL_RADIUS,
    variant: "transparent",
    color: disabled
      ? iconColour?.disabledIcon ?? iconColour?.normal
      : canHover
        ? iconColour?.hover
        : iconColour?.normal,
    style: {
      backgroundColor: canHover ? iconColour?.hoverBg ?? "#F0EEEB" : "transparent",
      border: inGroup
        ? "none"
        : canHover
          ? `1px solid ${iconColour?.border ?? "#E7E5E4"}`
          : "1px solid transparent",
      opacity: 1,
    },
  };
}

export function getEditorChromeTheme(isDark) {
  if (isDark) {
    return {
      /** Top bar + footer */
      bg: "#262626",
      /** Left tool rail — slightly recessed */
      sidebarBg: "#1C1C1C",
      /** Right inspector panel */
      asideBg: "#2E2E2E",
      /** Shell fill behind the viewport (when visible at edges / load) */
      workspaceBg: "#181818",
      /** Inspector tab strip */
      tabsListBg: "#262626",
      border: "#44403C",
      controlBorder: "#57534E",
      icon: "#A8A29E",
      iconHover: "#FAFAF9",
      hoverBg: "#383838",
      link: "#D6D3D1",
      linkHover: "#FAFAF9",
      title: "#FAFAF9",
      body: "#D6D3D1",
      muted: "#A8A29E",
      sectionLabel: "#A8A29E",
      disabledBg: "#333333",
      disabledIcon: "#78716C",
      headerShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
      /** Inset wells inside the right aside (collaborator list) */
      asideInsetBg: "#383838",
      /** Online / presence badge */
      presenceBadgeBg: "rgba(34, 197, 94, 0.18)",
      presenceBadgeText: "#4ADE80",
    };
  }
  return {
    bg: "#FFFFFF",
    sidebarBg: "#F5F4F2",
    asideBg: "#FFFFFF",
    workspaceBg: "#E8E4DE",
    tabsListBg: "#FAFAF9",
    border: "#E7E5E4",
    controlBorder: "#E7E5E4",
    icon: "#78716C",
    iconHover: "#1C1917",
    hoverBg: "#F0EEEB",
    link: "#78716C",
    linkHover: "#1C1917",
    title: "#1C1917",
    body: "#57534E",
    muted: "#78716C",
    sectionLabel: "#78716C",
    disabledBg: "#F5F5F4",
    disabledIcon: "#A8A29E",
    headerShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    asideInsetBg: "#FAFAF9",
    presenceBadgeBg: "#DCFCE7",
    presenceBadgeText: "#166534",
  };
}

/** Collaborators footer — derived from editor chrome (stone palette, WCAG-friendly muted text). */
export function getCollaboratorsChromeTheme(isDark) {
  const t = getEditorChromeTheme(isDark);
  return {
    heading: t.sectionLabel,
    badgeBg: t.presenceBadgeBg,
    badgeText: t.presenceBadgeText,
    listBg: t.asideInsetBg,
    listBorder: t.border,
    emptyText: t.muted,
    rowText: t.body,
    eye: t.muted,
    eyeHover: t.iconHover,
    buttonBg: t.bg,
    buttonBorder: t.controlBorder,
    buttonText: t.title,
  };
}

/** Scrollable collaborator list inside the aside inset well. */
export function collaboratorsListBoxStyle(theme) {
  return {
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "none",
    borderRadius: CONTROL_RADIUS,
    backgroundColor: theme.listBg,
    border: `1px solid ${theme.listBorder}`,
  };
}

/** Inspector-style uppercase section labels (Elements, Scene list, etc.) */
export function sectionLabelStyles(theme) {
  return {
    fontFamily: UI_FONT,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: theme?.sectionLabel ?? theme?.mutedTextColor ?? "#78716C",
  };
}

/** Left rail / toolbar icon button — subtle hover, no heavy border box */
export function chromeActionIconStyles(hover, theme) {
  return {
    width: CHROME_ICON_HIT,
    height: CHROME_ICON_HIT,
    minWidth: CHROME_ICON_HIT,
    minHeight: CHROME_ICON_HIT,
    borderRadius: CONTROL_RADIUS,
    border: "1px solid transparent",
    backgroundColor: hover ? theme.hoverBg : "transparent",
    color: hover ? theme.iconHover : theme.icon,
    transition: "background-color 120ms ease, color 120ms ease",
  };
}

export function getPreviewChromeIconColour(isDark) {
  const t = getEditorChromeTheme(isDark);
  return { normal: t.icon, hover: t.iconHover };
}
